<script setup lang="ts">
import { DialogModal, NoteComponent, type SharedBudgetPayload } from '@metapic-apps/shared'
import type { FormInstance, FormRules } from 'element-plus'
import { computed, reactive, ref, watch } from 'vue'

import { positiveNumberRule, requiredRule } from '@/lib/rules/common.rules'
import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, TitleComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{ isLoading: boolean; budget?: SharedBudgetPayload; currency?: string }>()
const isOpen = defineModel<boolean>('modelValue', { default: false })
const emits = defineEmits(['save'])
const createBudgetDraft = (budget?: SharedBudgetPayload): SharedBudgetPayload =>
  budget
    ? { ...budget, amount: { ...budget.amount } }
    : {
        id: Math.random(),
        title: '',
        amount: { amount: 0, currency: props.currency || '' },
        currency: props.currency || '',
        consumedBudget: 0,
        campaignsCount: 0,
        isCreated: 'empty',
      }
const formData = ref(createBudgetDraft(props.budget))
const ruleFormRef = ref<FormInstance>()
const infoMessage = computed(() => {
  if (formData.value.isCreated) {
    return null
  }
  if (props.budget?.amount.amount !== formData.value.amount.amount) {
    return 'Editing the shared budget may cause added campaigns to stop or reopen.'
  }

  return null
})
const rules = reactive<FormRules<SharedBudgetPayload>>({
  title: [
    { required: true, message: 'Title is required', trigger: 'blur' },
    { max: 255, message: 'Field must be shorter than 255 symbols', trigger: 'blur' },
  ],
  amount: [requiredRule, positiveNumberRule],
})
const submitForm = (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }

  return formEl.validate((valid) => {
    if (valid) {
      emits('save', createBudgetDraft(formData.value))
    }
  })
}

watch([() => props.budget, isOpen], ([, newIsOpen]) => {
  if (newIsOpen) {
    formData.value = createBudgetDraft(props.budget)
  }
})
</script>
<template>
  <DialogModal v-model="isOpen" width="400" destroy-on-close @before-close="isOpen = false">
    <template #header>
      <TypographyComponent variant="headline-medium"> {{ formData.isCreated === 'empty' ? 'New' : 'Edit' }} budget </TypographyComponent>
    </template>
    <el-form ref="ruleFormRef" :model="formData" :rules="rules" hide-required-asterisk>
      <BoxComponent :mt="24">
        <BoxComponent :mb="8">
          <TitleComponent title="Title" />
        </BoxComponent>
        <el-form-item prop="title">
          <InputComponent v-model="formData.title" name="title" />
        </el-form-item>
      </BoxComponent>
      <BoxComponent :mt="24">
        <BoxComponent :mb="8">
          <TitleComponent title="Amount" />
        </BoxComponent>
        <el-form-item prop="amount.amount">
          <InputComponent v-model.number="formData.amount.amount" name="amount">
            <template #suffix>
              <TypographyComponent variant="title-large"> {{ currency }} </TypographyComponent>
            </template>
          </InputComponent>
        </el-form-item>
      </BoxComponent>
      <BoxComponent v-if="infoMessage" :mt="24">
        <NoteComponent>{{ infoMessage }}</NoteComponent>
      </BoxComponent>
      <BoxComponent :mt="32">
        <FlexComponent :gap="8" justify="end" flex="1">
          <ButtonComponent variant="secondary" :disabled="isLoading" @click="isOpen = false"> Cancel </ButtonComponent>
          <ButtonComponent :is-loading="isLoading" data-test="submitFormBtn" @click.prevent="() => submitForm(ruleFormRef)">
            {{ formData.isCreated === 'empty' ? 'Save' : 'Edit' }}
          </ButtonComponent>
        </FlexComponent>
      </BoxComponent>
    </el-form>
  </DialogModal>
</template>
<style lang="scss" scoped></style>
